import React, { useState, useEffect, useContext } from "react"
import ScopeContext from "../contexts/ScopeContext"
import ErrorMessage from "./common/ErrorMessage"
import { beautifyDate } from "../utilities/dates"

const AddScoreModal = ({ onClose, quickAdd = false, presetDate = null }) => {
  const { addScore } = useContext(ScopeContext)

  const [date, setDate] = useState("")
  const [score, setScore] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Set default date to today or use preset date
  useEffect(() => {
    const defaultDate = presetDate || new Date().toISOString().split('T')[0]
    setDate(defaultDate)
  }, [presetDate])

  const handleDateChange = (event) => {
    const inputDate = new Date(event.target.value)
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)

    if (inputDate >= tomorrow) {
      setError("Date cannot be in the future")
    } else {
      setError(null)
      setDate(event.target.value)
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!date || !score) return

    setIsLoading(true)
    setError(null)

    try {
      await addScore(date, score === "delete" ? null : parseInt(score))
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add score. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleScoreChange = async (event) => {
    const selectedScore = event.target.value
    setScore(selectedScore)

    // Auto-submit only for quick add modal
    if (quickAdd && selectedScore && date) {
      setIsLoading(true)
      setError(null)

      try {
        await addScore(date, selectedScore === "delete" ? null : parseInt(selectedScore))
        onClose()
      } catch (err) {
        setError(err.response?.data?.error || "Failed to add score. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }
  }

  // Score options: 1-6 for guesses, 7 for not completed, 8 for failed
  const scoreOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
    { value: 6, label: "6" },
    { value: 8, label: "Failed" },
    { value: "delete", label: "Not completed" },
  ]

  const modalTitle = quickAdd && date
    ? `Add score for ${beautifyDate(date, true, false)}`
    : "Add score"

  return (
    <div className="scoreModal">
      <h2 style={{ marginTop: 0 }}>{modalTitle}</h2>

      <ErrorMessage message={error} onDismiss={() => setError(null)} />

      <form onSubmit={handleSubmit}>
        {!quickAdd && (
          <div className="formGroup">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={handleDateChange}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        )}

        <div className="formGroup">
          <label>Score</label>
          <select
            value={score}
            onChange={handleScoreChange}
            required
            disabled={isLoading}
          >
            <option value="" disabled>Select a score</option>
            {scoreOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {!quickAdd && (
          <div className="modal-button-row">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!date || !score || isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </form>
    </div>
  )
}

export default AddScoreModal;
